import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";


function QuestionInput({ q, a = {}, onChange, locked }) {
  const set = (k, v) => onChange({ ...a, [k]: v });
  return (
    <div className="card-luxe p-5" data-testid={`cq-${q.num}`}>
      <p className="text-[10px] uppercase tracking-[0.22em] text-[#DCB457]">Question {q.num} · {q.title}</p>
      <p className="font-serif-luxe text-xl mt-2 leading-snug">{q.text}</p>
      {q.client_hint && <p className="text-xs text-white/50 mt-2">{q.client_hint}</p>}
      <div className="mt-4 space-y-2">
        {q.choices?.map((c, i) => (
          <label key={c} className={`flex items-start gap-3 border px-3 py-3 text-sm cursor-pointer transition-colors ${a.choice === c ? "border-[#DCB457] bg-[#DCB457]/10" : "border-white/10"}`}>
            <input type="radio" name={q.id} disabled={locked} checked={a.choice === c} onChange={() => set("choice", c)} className="mt-0.5" data-testid={`cq-${q.num}-choice-${i}`} /><span><b>{String.fromCharCode(65 + i)}.</b> {c}</span>
          </label>))}
        {q.allow_other && <input disabled={locked} placeholder="Autre / précisez" value={a.other || ""} onChange={(e) => set("other", e.target.value)} className="input-luxe w-full" />}
        {(q.fields || []).map((f) => <input key={f} disabled={locked} placeholder={f} value={a[f] || ""} onChange={(e) => set(f, e.target.value)} className="input-luxe w-full" data-testid={`cq-${q.num}-field-${f}`} />)}
        {!q.choices?.length && !(q.fields || []).length && <textarea disabled={locked} rows={2} placeholder="La véritable réponse" value={a.value || ""} onChange={(e) => set("value", e.target.value)} className="input-luxe w-full" data-testid={`cq-${q.num}-value`} />}
      </div>
    </div>
  );
}

function Questionnaire({ id }) {
  const [d, setD] = useState(null);
  const [answers, setAnswers] = useState({});
  const [custom, setCustom] = useState([]);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  const load = useCallback(() => api.get(`/me/animations/${id}`).then((r) => { setD(r.data); setAnswers(r.data.answers || {}); setCustom(r.data.custom_questions || []); }), [id]);
  useEffect(() => { load(); }, [load]);
  const save = useCallback(async (a, c, silent) => {
    setSaving(true);
    try { const r = await api.put(`/me/animations/${id}`, { answers: a, custom_questions: c }); setD(r.data); if (!silent) toast.success("Réponses enregistrées — vous pouvez continuer plus tard."); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  }, [id]);
  const queue = (a, c) => { clearTimeout(timer.current); timer.current = setTimeout(() => save(a, c, true), 1200); };
  if (!d) return <p className="text-white/45 italic">Chargement…</p>;
  const locked = d.locked;
  const pct = d.total ? Math.round((d.answered / d.total) * 100) : 0;
  const validate = async () => {
    const missing = d.total - d.answered;
    const warn = missing > 0 ? `Attention : ${missing} question(s) sur ${d.total} sans réponse. Elles ne pourront pas être utilisées pendant l'animation.\n\n` : "";
    if (!window.confirm(`${warn}Une fois validées, vos réponses seront transmises à My Animation pour préparer l'animation et ne seront plus modifiables. Confirmer ?`)) return;
    clearTimeout(timer.current);
    await save(answers, custom, true);
    try { await api.post(`/me/animations/${id}/validate`); toast.success("Merci ! Vos réponses ont bien été transmises à My Animation."); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const main = d.questions.filter((q) => q.category === "principale");
  const bonus = d.questions.filter((q) => q.category !== "principale");
  const onA = (q, v) => { const n = { ...answers, [q.id]: v }; setAnswers(n); queue(n, custom); };
  const onC = (c) => { setCustom(c); queue(answers, c); };
  return (
    <div className="space-y-8" data-testid="client-quiz">
      <div>
        <h1 className="font-serif-luxe text-3xl md:text-4xl">{d.template_name}</h1>
        <p className="text-white/60 mt-1">{d.subtitle}</p>
        {d.instruction && <p className="text-sm text-white/55 mt-3">{d.instruction}</p>}
      </div>
      <div className="card-luxe p-5 sticky top-2 z-10 bg-black/90 backdrop-blur" data-testid="client-quiz-progress">
        <p className="text-[10px] uppercase tracking-[0.22em] text-white/45">Votre questionnaire</p>
        <div className="h-2 bg-white/10 mt-3"><div className="h-2 bg-[#DCB457] transition-all" style={{ width: `${pct}%` }} /></div>
        <p className="text-sm mt-2"><b>{d.answered} / {d.total}</b> questions complétées{saving ? " · enregistrement…" : ""}</p>
      </div>
      {locked && <div className="border border-[#DCB457]/40 bg-[#DCB457]/10 px-4 py-3 text-sm inline-flex gap-2 items-center" data-testid="client-quiz-locked"><Lock size={14} />{d.status === "valide_client" ? "Merci ! Vos réponses ont bien été transmises à My Animation." : "Questionnaire verrouillé — préparation de votre animation en cours."}</div>}
      {[["Questions principales", main], ["Questions bonus", bonus]].map(([t, list]) => list.length > 0 && (
        <section key={t} className="space-y-4"><h2 className="font-serif-luxe text-2xl text-[#DCB457]">{t}</h2>{list.map((q) => <QuestionInput key={q.id} q={q} a={answers[q.id]} locked={locked} onChange={(v) => onA(q, v)} />)}</section>))}
      <section className="space-y-4" data-testid="client-custom-questions">
        <h2 className="font-serif-luxe text-2xl text-[#DCB457]">Vos questions personnalisées</h2>
        <p className="text-sm text-white/55">Vous souhaitez ajouter vos propres questions ?</p>
        {custom.map((c, i) => (
          <div key={c.id || i} className="card-luxe p-4 space-y-2">
            {["question", "answer", "comment"].map((k) => <input key={k} disabled={locked} placeholder={{ question: "Question proposée", answer: "Réponse", comment: "Commentaire éventuel" }[k]} value={c[k] || ""} onChange={(e) => onC(custom.map((x, n) => (n === i ? { ...x, [k]: e.target.value } : x)))} className="input-luxe w-full" data-testid={`ccq-${i}-${k}`} />)}
            {!locked && <button onClick={() => onC(custom.filter((_, n) => n !== i))} className="text-xs text-white/40 inline-flex gap-1"><Trash2 size={12} />Retirer</button>}
          </div>))}
        {!locked && <button onClick={() => setCustom([...custom, { question: "", answer: "", comment: "" }])} className="btn-ghost-gold !py-2 !text-[10px] inline-flex gap-1.5" data-testid="ccq-add"><Plus size={12} />Ajouter une question</button>}
      </section>
      {!locked && (
        <div className="flex flex-col sm:flex-row gap-3 pb-10">
          <button onClick={() => save(answers, custom)} className="btn-ghost-gold" data-testid="client-quiz-save">Enregistrer et continuer plus tard</button>
          <button onClick={validate} className="btn-gold inline-flex gap-2 justify-center" data-testid="client-quiz-validate"><CheckCircle2 size={15} />Valider définitivement mes réponses</button>
        </div>
      )}
    </div>
  );
}

export default function JeuxAnimations() {
  const { id } = useParams();
  const [list, setList] = useState(null);
  useEffect(() => { if (!id) api.get("/me/animations").then((r) => setList(r.data)).catch(() => setList([])); }, [id]);
  if (id) return <Questionnaire id={id} />;
  return (
    <div data-testid="client-games">
      <h1 className="font-serif-luxe text-3xl md:text-4xl">Jeux & Animations</h1>
      {!list ? null : !list.length ? <p className="text-white/50 mt-6">Aucun questionnaire pour le moment.</p> : (
        <div className="space-y-3 mt-6">{list.map((a) => (
          <Link key={a.id} to={`/espace-client/jeux/${a.id}`} className="card-luxe p-5 flex flex-wrap items-center gap-3" data-testid={`client-game-${a.id}`}>
            <div className="flex-1"><p className="font-serif-luxe text-xl">{a.template_name}</p><p className="text-xs text-white/50">{a.subtitle}</p>{a.event_date && <p className="text-xs text-[#DCB457] mt-1" data-testid={`client-game-date-${a.id}`}>Événement du {a.event_date.slice(0, 10).split("-").reverse().join("/")}</p>}</div>
            <span className="text-sm">{a.answered} / {a.total}</span>{a.locked && <Lock size={14} className="text-[#DCB457]" />}
          </Link>))}</div>
      )}
    </div>
  );
}
